/* eslint-disable prettier/prettier */
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import QuickSearch from "../../components/QuickSearch";
import {
  useCheckOrderStatus,
  useGetAllCretaedMCAOrder,
} from "../../queries/onboarding";
import { CreateDownloadOrderType } from "../../types/onBoardingModel";
import {
  SubscriptionHandle,
  subscribeToNotifications,
} from "../../api/notifications";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function parseOrderDate(dateStr: string): Date | null {
  if (!dateStr || dateStr === "-") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const match = dateStr.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (match) {
    const monthIdx = MONTHS.findIndex(
      (m) => m.toLowerCase() === match[2].toLowerCase(),
    );
    if (monthIdx !== -1)
      return new Date(Number(match[3]), monthIdx, Number(match[1]));
  }
  return null;
}

function formatOrderDate(dateStr: string): string {
  const date = parseOrderDate(dateStr);
  if (!date) return "-";
  return `${String(date.getDate()).padStart(2, "0")} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

const MCAOrders: React.FC = () => {
  const navigate = useNavigate();
  const { data: _allmcaorders, isFetching: isOrdersfetched } =
    useGetAllCretaedMCAOrder();
  const { mutate: refetchOrderStatus } = useCheckOrderStatus();
  const gridApi = useRef<AgGridReact>(null);
  const [searchResultCompanies, setSearchResultCompanies] = useState(0);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showRecentPending, setShowRecentPending] = useState(false);
  const showRecentPendingRef = useRef(false);
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, string>
  >({});
  const sseRef = useRef<SubscriptionHandle | null>(null);
  const allMcaOrders = _allmcaorders as CreateDownloadOrderType[];

  const rowsData = useMemo(
    () =>
      allMcaOrders?.map((mcaorder: CreateDownloadOrderType) => ({
        cin: mcaorder.cin,
        orderStatus: statusOverrides[mcaorder.cin] ?? mcaorder.status,
        orderDate: formatOrderDate(mcaorder.orderDate),
        companyName: mcaorder.name ? mcaorder.name : "-",
        documentType: mcaorder.documentType,
      })),
    [_allmcaorders, statusOverrides],
  );

  const recentPendingCount = useMemo(() => {
    return (rowsData ?? []).filter(({ orderDate, orderStatus }) => {
      if (orderStatus?.toLowerCase() !== "pending") return false;
      const date = parseOrderDate(orderDate);
      return !!date && (Date.now() - date.getTime()) / 86_400_000 <= 30;
    }).length;
  }, [rowsData]);

  function toggleRecentPending() {
    const next = !showRecentPendingRef.current;
    showRecentPendingRef.current = next;
    setShowRecentPending(next);
    gridApi.current?.api.onFilterChanged();
  }

  useEffect(() => {
    sseRef.current = subscribeToNotifications({
      eventTypes: ["MCA_ORDER"],
      onMessage: (n) => {
        const {
          downloaded = [],
          failed = [],
          pending = [],
        } = (n.eventData ?? {}) as {
          downloaded?: string[];
          failed?: string[];
          pending?: string[];
        };
        setStatusOverrides((prev) => {
          const next = { ...prev };
          downloaded.forEach((cin) => {
            next[cin] = "completed";
          });
          failed.forEach((cin) => {
            next[cin] = "failed";
          });
          pending.forEach((cin) => {
            next[cin] = "pending";
          });
          return next;
        });
        toast(<span dangerouslySetInnerHTML={{ __html: n.message }} />, {
          type: n.eventLevel === "ERROR" ? "error" : "success",
          autoClose: 5000,
        });
      },
    });
    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
  }, []);
  useEffect(() => {
    setSearchResultCompanies(
      gridApi?.current?.api?.getDisplayedRowCount() || 0,
    );
  }, [gridApi?.current?.api]);

  const components = useMemo(
    () => ({
      companyName: MCAOrders,
    }),
    [],
  );
  const columnDefs: ColDef[] = [
    {
      headerName: "Company CIN",
      field: "cin",
      cellRenderer: "cin",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Company Name",
      field: "companyName",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "MCA Order Date",
      field: "orderDate",
      sort: "desc",
      filter: "agSetColumnFilter",
      filterValueGetter: (params) => {
        const date = parseOrderDate(params.data?.orderDate);
        if (!date) return null;
        return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
      },
      filterParams: {
        // sort set values chronologically in the filter panel
        comparator: (a: string, b: string) => {
          const ta = parseOrderDate(`01 ${a}`)?.getTime() ?? 0;
          const tb = parseOrderDate(`01 ${b}`)?.getTime() ?? 0;
          return ta - tb;
        },
      },
      comparator: (valueA: string, valueB: string) => {
        const a = parseOrderDate(valueA)?.getTime() ?? 0;
        const b = parseOrderDate(valueB)?.getTime() ?? 0;
        return a - b;
      },
    },
    {
      headerName: "MCA Order Status",
      field: "orderStatus",
    },
    {
      headerName: "Document Type",
      field: "documentType",
    },
  ];
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: true,
      minWidth: 150,
      flex: 1,
    }),
    [],
  );

  function refreshOrders() {
    refetchOrderStatus();
    toast("Refresh request has been made", {
      autoClose: 2000,
      type: "success",
    });
  }
  return (
    <>
      <div className="flex items-center justify-between py-5">
        <div>
          <h1 className="text-2xl font-medium">MCA Orders</h1>
          <p className="pb-2 text-sm font-medium text-gray-500">
            {searchResultCompanies} companies
          </p>
        </div>
        <div className="flex flex-row items-center gap-4">
          <div className="relative group">
            <div
              className={`p-2 rounded-sm cursor-pointer h-9 border transition-colors ${
                showRecentPending
                  ? "bg-amber-400 text-white border-amber-400"
                  : "bg-amber-50 text-amber-700 border-amber-300"
              }`}
              onClick={toggleRecentPending}
            >
              Pending last 30 days
              {recentPendingCount > 0 ? ` (${recentPendingCount})` : ""}
            </div>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-56 rounded bg-gray-800 px-3 py-2 text-xs text-white shadow-lg text-center pointer-events-none">
              Show only pending orders placed in the last 30 days
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
            </div>
          </div>
          <div
            className="p-2 text-white rounded-sm cursor-pointer bg-orange-501 h-9"
            onClick={() => navigate("/createOrder")}
          >
            Create MCA Order
          </div>
          <div
            className="p-2 text-white rounded-sm cursor-pointer bg-orange-501 h-9"
            onClick={() => refreshOrders()}
          >
            Refresh Order
          </div>
          <div>
            <QuickSearch
              placeholder="Search"
              onChange={(e) => {
                gridApi.current!.api.setGridOption(
                  "quickFilterText",
                  e.target.value,
                );
                setSearchResultCompanies(
                  gridApi.current?.api.getDisplayedRowCount() || 0,
                );
              }}
            />
          </div>
        </div>
      </div>
      <div
        className={`${isDarkTheme ? "ag-theme-quartz-dark" : "ag-theme-quartz"} h-[calc(100vh-22vh)]`}
      >
        <AgGridReact
          ref={gridApi}
          components={components}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={rowsData}
          suppressRowTransform={true}
          pagination={true}
          animateRows={true}
          isExternalFilterPresent={() => showRecentPendingRef.current}
          doesExternalFilterPass={(node) => {
            const { orderDate, orderStatus } = node.data || {};
            if (orderStatus?.toLowerCase() !== "pending") return false;
            const date = parseOrderDate(orderDate);
            return !!date && (Date.now() - date.getTime()) / 86_400_000 <= 30;
          }}
          getRowStyle={(params) => {
            const { orderDate, orderStatus } = params.data || {};
            if (orderStatus?.toLowerCase() === "pending") {
              const date = parseOrderDate(orderDate);
              if (date) {
                const days = (Date.now() - date.getTime()) / 86_400_000;
                if (days <= 30) return { background: "#fef3c7" };
                if (days > 31) return { background: "#fee2e2" };
              }
            }
            return {
              background: params.rowIndex % 2 === 0 ? "#f8f8f8" : "#ffffff",
            };
          }}
        />
      </div>
    </>
  );
};

export default MCAOrders;
